import {
  AnalyticsEvent,
  AnalyticsEventNameType,
  DataSource,
  DefaultApi,
  GetClientFlags200Response,
} from "@coeff/api";
import { message } from "antd";
import React from "react";

import { MessageProvider } from "../../../../../gsheets-addon/src/client/components/MessageProvider";
import { CoeffGasFunctions } from "../../../../../gsheets-addon/src/client/server-interface";
import { EventProperties } from "../../../../../gsheets-addon/src/client/types";
import { StatsigSynchronousProvider } from "../../../../../gsheets-addon/src/client/views/StatsigSynchronousProvider";
import { ApiClientContext, EventManagerContext, GasServerContext } from "../../globals/contexts";
import type { PpressContext } from "../../ppress/context";

export interface BaseAuthorizeLocationState {
  fromConnectedSource?: boolean;
  fromOnboarding?: boolean;
  isEditConnectionId?: string;
  dataSource?: DataSource;
}

export interface WithPostDataSourceCreationProps {
  initPostDataSourceCreation: (config: {
    dataSource: DataSource;
    onComplete: (dataSource: DataSource) => void;
  }) => void;
  postDataSourceCreation?: (dataSource: DataSource) => void;
  onClose?: () => void;
}

export const QueryCacheKey = {
  DATA_SOURCE: "DATA_SOURCE",
  DATA_SOURCES: "DATA_SOURCES",
  OAUTH_ATTEMPT: "OAUTH_ATTEMPT",
  LOOKER_APP_INFO: "LOOKER_APP_INFO",
  LOOKER_REQUEST_URL: "LOOKER_REQUEST_URL",
};

// Full AuthContext for auth components
export interface AuthContextValue {
  // api client
  apiClient: DefaultApi;

  // em context
  track: (event: AnalyticsEventNameType, extraProperties?: AnalyticsEvent) => void;

  // gas (optional — not available in webapp)
  gasServer?: CoeffGasFunctions;

  // event props
  eventProps: EventProperties & AnalyticsEvent;
  clientFlags: GetClientFlags200Response;

  analyticsContext?: {
    fromConnectedSource?: boolean;
    fromOnboarding?: boolean;
  };
  openSupportChat?: () => Promise<{ blocked: boolean }>;

  components?: {
    PpressContext?: typeof PpressContext;
  };

  router?: {
    back?: () => void;
    push?: (path: string) => void;
  };

  // ngrok API base URL for local OAuth redirect proxying (staging only)
  coeffDevRedirectBaseUrl?: string;

  // Utility functions for OAuth and PPress - provided by consuming app
  getOAuthAuthorizationUrl?: (
    apiClient: DefaultApi,
    params: {
      email?: string;
      oauthAttemptId?: string;
      dataSourceId?: string;
      dataSourceType: string;
      ppress_params?: Record<string, any>;
      ppress_metadata?: Record<string, any>;
      from?: "addon" | "webapp";
      coeffDevRedirectBaseUrl?: string;
    }
  ) => Promise<string>;
  buildPPressMetadata?: (params: {
    dataSourceType: string;
    ppress_params?: Record<string, any>;
  }) => Record<string, any>;
  getDataSourceApiErrorMessage?: (error: any) => string;
  navigateOnDataSourceUpsert?: (dataSource: DataSource) => void;
  renderConnectionHelpCard?: (args: { dataSourceType: string }) => React.ReactNode;
  dataSource?: DataSource;
  // Post-data-source-creation hook. Provided by the host (addon withPostDataSourceCreation
  // HOC, or webapp). Used by connectors (e.g. Tableau) rendered inside the PPress auth form
  // that create the data source themselves and need the post-creation flow.
  initPostDataSourceCreation?: (config: {
    dataSource: DataSource;
    onComplete: (dataSource: DataSource) => void;
  }) => void;
  sentryCapture?: (props: {
    context?: { [x: string]: unknown };
    error: unknown;
    name?: string;
    screen?: string;
  }) => void;
}

export const AuthContext = React.createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  value: AuthContextValue;
  children: React.ReactNode;
}

/**
 * AuthProvider - Context provider with nested providers
 * The addon provides all necessary values including queryClient, theme, etc.
 */

export const AuthProvider: React.FC<AuthProviderProps> = ({ value, children }) => {
  const { apiClient, track, gasServer, eventProps, clientFlags } = value;

  return (
    <StatsigSynchronousProvider clientFlags={clientFlags} eventProps={eventProps}>
      <ApiClientContext.Provider value={apiClient}>
        <GasServerContext.Provider value={gasServer != null ? { gasServer } : null}>
          <EventManagerContext.Provider value={{ track }}>
            <MessageProvider message={message} track={track}>
              <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
            </MessageProvider>
          </EventManagerContext.Provider>
        </GasServerContext.Provider>
      </ApiClientContext.Provider>
    </StatsigSynchronousProvider>
  );
};

export const useAuthContext = (): AuthContextValue => {
  const context = React.useContext(AuthContext);
  if (!context) {
    // Return empty object instead of throwing to allow components to work without provider
    return {} as AuthContextValue;
  }
  return context;
};

// Re-export utility functions for convenience
export {
  getOAuthAuthorizationUrl,
  buildPPressMetadata,
  getDataSourceApiErrorMessage,
} from "../utils";
